import React from 'react';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageTitle from 'src/components/page-title/index';
import Divider from 'src/components/divider';
import ETLUpdate from 'src/components/etl-update';
import FitHistoryPage from 'src/pages/reports/fit';
import InactivationPage from 'src/pages/reports/inactivation';
import './style.scss';

/**
 * Reports main page component — renders Fits History or Inactive Profiles based on the active route.
 *
 * @param {Object} props - The component props.
 * @param {string} props.etlDate - Last ETL update datetime.
 * @param {Array} props.fitData - Fit History records.
 * @param {Object} props.fitPagination - Fit History pagination metadata.
 * @param {Object} props.fitFilterOptions - Fit History filter options.
 * @param {boolean} props.fitFetching - Whether Fit History is loading.
 * @param {boolean} props.fitExporting - Whether Fit History export is in progress.
 * @param {Function} props.onFitFilterChange - Callback for Fit History filter changes.
 * @param {Function} props.onFitPaginationChange - Callback for Fit History pagination changes.
 * @param {Function} props.onExportFit - Callback for Fit History export.
 * @param {Array} props.inactivationData - Inactive Profiles records.
 * @param {Object} props.inactivationPagination - Inactive Profiles pagination metadata.
 * @param {Object} props.inactivationFilterOptions - Inactive Profiles filter options.
 * @param {boolean} props.inactivationFetching - Whether Inactive Profiles is loading.
 * @param {boolean} props.inactivationExporting - Whether Inactive Profiles export is in progress.
 * @param {Function} props.onInactivationFilterChange - Callback for Inactive Profiles filter changes.
 * @param {Function} props.onInactivationPaginationChange - Callback for Inactive Profiles pagination changes.
 * @param {Function} props.onExportInactivation - Callback for Inactive Profiles export.
 * @returns {JSX.Element} The ReportsPage component.
 */
function ReportsPage({
  etlDate,
  fitData,
  fitPagination,
  fitFilterOptions,
  fitFetching,
  fitExporting,
  onFitFilterChange,
  onFitPaginationChange,
  onFitSortChange,
  onExportFit,
  inactivationData,
  inactivationPagination,
  inactivationFilterOptions,
  inactivationFetching,
  inactivationExporting,
  onInactivationFilterChange,
  onInactivationPaginationChange,
  onInactivationSortChange,
  onExportInactivation,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const activeTab = location.pathname.includes('/reports/inactivation') ? 'inactivation' : 'fit';
  const pageTitle = activeTab === 'inactivation' ? t('reportsTitle.inactivation') : t('reportsTitle.fit');

  return (
    <div className='reports-page'>
      <div className='reports-page__header'>
        <PageTitle title={pageTitle} />
        <ETLUpdate date={etlDate} />
      </div>
      <Divider />
      {activeTab === 'fit' ? (
        <FitHistoryPage
          data={fitData}
          pagination={fitPagination}
          filterOptions={fitFilterOptions}
          loading={fitFetching}
          exporting={fitExporting}
          onFilterChange={onFitFilterChange}
          onPaginationChange={onFitPaginationChange}
          onSortChange={onFitSortChange}
          onExport={onExportFit}
        />
      ) : (
        <InactivationPage
          data={inactivationData}
          pagination={inactivationPagination}
          filterOptions={inactivationFilterOptions}
          loading={inactivationFetching}
          exporting={inactivationExporting}
          onFilterChange={onInactivationFilterChange}
          onPaginationChange={onInactivationPaginationChange}
          onSortChange={onInactivationSortChange}
          onExport={onExportInactivation}
        />
      )}
    </div>
  );
}

ReportsPage.defaultProps = {
  etlDate: '',
  fitData: [],
  fitPagination: { currentPage: 1, totalCount: 0, limit: 10 },
  fitFilterOptions: {},
  fitFetching: false,
  fitExporting: false,
  onFitFilterChange: noop,
  onFitPaginationChange: noop,
  onFitSortChange: noop,
  onExportFit: noop,
  inactivationData: [],
  inactivationPagination: { currentPage: 1, totalCount: 0, limit: 10 },
  inactivationFilterOptions: {},
  inactivationFetching: false,
  inactivationExporting: false,
  onInactivationFilterChange: noop,
  onInactivationPaginationChange: noop,
  onInactivationSortChange: noop,
  onExportInactivation: noop,
};

export default ReportsPage;
